import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, NavLink, useLocation } from 'react-router-dom'

import { useSiteToggles } from '../../hooks/useSiteToggles'
import { localizedNumber } from '../../lib/site'
import { appVersionLine } from '../../lib/version'
import { useAuthStore } from '../../store/authStore'
import ParrotMark from '../common/ParrotMark'
import EnamadSeal from './EnamadSeal'
import Mascot from './Mascot'

/**
 * The public site's frame (design "Tooti Web"): the sticky header with its
 * scroll-progress strip, the four-page nav, the language and theme
 * toggles, the login button, the footer — and, on the landing only, the
 * parrot guide that follows the scroll and comments on the section in
 * view. Every public page (Landing, About, Contact, Privacy, the login,
 * the download page) renders inside it; the signed-in app keeps its own
 * AppShell.
 *
 * The design decides "wide" at 900px by measuring the window; here that
 * is the `min-[900px]` variant, so the server render and the first paint
 * already agree. The language toggle is the same manual choice as the one
 * in Settings (`prefsStore.setLanguage`), so it locks out the geo-IP
 * default the same way — and it is absent altogether for a visitor whose
 * country decides the language for them (lib/geoLocale.js), which is also
 * the visitor the footer's Enamad seal is there for.
 */

export const CONTAINER = 'mx-auto w-full max-w-[1140px] px-4 sm:px-5'

const NAV_ITEM =
  'flex h-[42px] items-center whitespace-nowrap rounded-xl px-[15px] font-display text-base font-bold transition hover:-translate-y-0.5 hover:bg-surface-2 hover:text-ink'
const ROUND_BUTTON =
  'inline-flex h-11 items-center justify-center rounded-full border-2 border-line bg-surface font-display text-[15px] font-bold text-ink-2 transition hover:-translate-y-0.5 hover:text-ink'
const LOGIN_BUTTON =
  'site-btn h-[46px] items-center justify-center rounded-[14px] bg-brand-600 px-[22px] font-display text-base font-extrabold text-white [--press:var(--color-brand-deep)] dark:text-slate-900'

function usePages() {
  const { t } = useTranslation('site')
  return [
    { to: '/', label: t('nav.home'), end: true },
    { to: '/contact', label: t('nav.contact') },
    { to: '/privacy', label: t('nav.privacy') },
  ]
}

/**
 * Scroll bookkeeping for the header strip and the guide: which section
 * carries the reading line (62% down the viewport, as the design measures
 * it) and how far down the page is. Written straight to the strip's
 * style — a width that changes on every scroll event is not React state.
 */
function useScrollGuide(enabled, fillRef) {
  const [guide, setGuide] = useState({ key: 'hero', visible: false })

  useEffect(() => {
    if (typeof window === 'undefined') return undefined
    let frame = 0
    const tick = () => {
      frame = 0
      const doc = document.documentElement
      const max = Math.max(1, doc.scrollHeight - doc.clientHeight)
      const top = doc.scrollTop || window.scrollY || 0
      const progress = Math.min(1, top / max) * 100
      if (fillRef.current) fillRef.current.style.width = `${progress}%`
      if (!enabled) return
      const line = doc.clientHeight * 0.62
      let key = 'hero'
      document.querySelectorAll('[data-guide]').forEach((el) => {
        const rect = el.getBoundingClientRect()
        if (rect.top <= line && rect.bottom > line * 0.4) key = el.getAttribute('data-guide')
      })
      const visible = progress >= 4
      setGuide((prev) => (prev.key === key && prev.visible === visible ? prev : { key, visible }))
    }
    const schedule = () => {
      if (!frame) frame = window.requestAnimationFrame(tick)
    }
    window.addEventListener('scroll', schedule, { passive: true })
    window.addEventListener('resize', schedule)
    tick()
    return () => {
      window.removeEventListener('scroll', schedule)
      window.removeEventListener('resize', schedule)
      if (frame) window.cancelAnimationFrame(frame)
    }
  }, [enabled, fillRef])

  return guide
}

function Guide({ guideKey, visible }) {
  const { t } = useTranslation('site')
  return (
    <div
      aria-hidden="true"
      className="pointer-events-none fixed bottom-[18px] start-[18px] z-50 hidden items-end gap-2.5 transition-opacity duration-300 min-[420px]:flex"
      style={{ opacity: visible ? 1 : 0 }}
    >
      <div
        className="flex size-[78px] items-center justify-center rounded-full border-2 border-line bg-surface shadow-[0_5px_0_var(--color-line)]"
        style={{ animation: 't-bob 3.2s ease-in-out infinite' }}
      >
        <Mascot size={58} variant="blue" />
      </div>
      <div
        key={guideKey}
        className="hidden max-w-[250px] rounded-[18px] rounded-es-[4px] border-2 border-line bg-surface px-[15px] py-3 font-display text-[15.5px] font-bold leading-[1.45] text-ink shadow-[0_5px_0_var(--color-line)] min-[620px]:block"
        style={{ animation: 't-pop .32s ease-out both' }}
      >
        {t(`guide.${guideKey}`)}
      </div>
    </div>
  )
}

export default function SiteShell({ title, guide = false, children }) {
  const { t } = useTranslation('site')
  const { pathname } = useLocation()
  const pages = usePages()
  // Iran is served the Persian site and is offered no way out of it (languageLocked).
  const { language, dark, languageLocked, toggleLanguage, toggleTheme } = useSiteToggles()
  // Signed in, the header's button is the account hub — the website's login
  // exists for the subscription, the profile and support (lib/siteMode.js).
  const authenticated = useAuthStore((s) => s.status) === 'authenticated'
  const loginTo = authenticated ? '/account' : '/auth'
  const loginLabel = authenticated ? t('nav.account') : t('nav.login')
  const [menuOpen, setMenuOpen] = useState(false)
  const fillRef = useRef(null)
  const scroll = useScrollGuide(guide, fillRef)

  useEffect(() => {
    if (title && typeof document !== 'undefined') document.title = title
  }, [title])

  // A navigation closes the phone menu; the route it opened is on screen.
  useEffect(() => {
    setMenuOpen(false)
  }, [pathname])

  return (
    <div className="site-motion relative flex min-h-dvh flex-col bg-ground text-ink [overflow-x:clip]">
      <a
        href="#site-main"
        className="sr-only z-50 rounded-xl bg-brand-600 px-4 py-2 font-display font-bold text-white focus:not-sr-only focus:fixed focus:start-4 focus:top-4 dark:text-slate-900"
      >
        {t('nav.skip')}
      </a>
      <header className="sticky top-0 z-40 border-b-2 border-line bg-header-bg backdrop-blur-[14px]">
        <div className="h-1 bg-surface-2">
          <div
            ref={fillRef}
            data-site-progress
            className="h-1 w-0 rounded-e-full bg-[linear-gradient(90deg,var(--color-accent),var(--color-site-good),var(--color-sky))]"
          />
        </div>
        <div className={`${CONTAINER} flex items-center gap-3 py-2.5`}>
          <Link to="/" className="flex shrink-0 items-center gap-2.5 transition hover:-rotate-3 hover:scale-105">
            <ParrotMark size={38} />
            <span className="font-display text-2xl font-extrabold tracking-[-0.2px]">{t('brand')}</span>
          </Link>

          <nav aria-label={t('nav.ariaLabel')} className="hidden flex-grow justify-center gap-1 min-[900px]:flex">
            {pages.map(({ to, label, end }) => (
              <NavLink
                key={to}
                to={to}
                end={end}
                className={({ isActive }) => `${NAV_ITEM} ${isActive ? 'text-ink' : 'text-ink-2'}`}
              >
                {label}
              </NavLink>
            ))}
          </nav>
          <span className="flex-grow min-[900px]:hidden" />

          <div className="flex shrink-0 items-center gap-2">
            {!languageLocked && (
              <button
                type="button"
                onClick={toggleLanguage}
                aria-label={t('nav.otherLanguageLabel')}
                lang={language === 'fa' ? 'en' : 'fa'}
                className={`${ROUND_BUTTON} px-3.5 tracking-[0.3px] hover:-rotate-3`}
              >
                {t('nav.otherLanguage')}
              </button>
            )}
            <button
              type="button"
              onClick={toggleTheme}
              aria-label={dark ? t('nav.toLight') : t('nav.toDark')}
              className={`${ROUND_BUTTON} w-11 hover:rotate-[22deg]`}
            >
              {dark ? (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <circle cx="12" cy="12" r="4" />
                  <path d="M12 2v2m0 16v2M4.9 4.9l1.4 1.4m11.4 11.4 1.4 1.4M2 12h2m16 0h2M4.9 19.1l1.4-1.4m11.4-11.4 1.4-1.4" />
                </svg>
              ) : (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8z" />
                </svg>
              )}
            </button>
            <Link to={loginTo} className={`${LOGIN_BUTTON} hidden min-[900px]:inline-flex`}>
              {loginLabel}
            </Link>
            <button
              type="button"
              onClick={() => setMenuOpen((open) => !open)}
              aria-expanded={menuOpen}
              aria-controls="site-menu"
              aria-label={menuOpen ? t('nav.closeMenu') : t('nav.menu')}
              className="flex size-[46px] flex-col items-center justify-center gap-[5px] rounded-[14px] border-2 border-line bg-surface min-[900px]:hidden"
            >
              <span className="h-[2.5px] w-[18px] rounded-sm bg-ink" />
              <span className="h-[2.5px] w-[18px] rounded-sm bg-ink" />
              <span className="h-[2.5px] w-[18px] rounded-sm bg-ink" />
            </button>
          </div>
        </div>

        {menuOpen && (
          <div
            id="site-menu"
            className="flex flex-col gap-1.5 border-t-2 border-line bg-surface px-4 pb-[18px] pt-3 min-[900px]:hidden"
            style={{ animation: 't-rise .28s ease-out both' }}
          >
            {pages.map(({ to, label, end }) => (
              <NavLink
                key={to}
                to={to}
                end={end}
                className="flex h-[50px] items-center rounded-[14px] px-3.5 font-display text-lg font-bold text-ink hover:bg-surface-2"
              >
                {label}
              </NavLink>
            ))}
            <Link
              to={loginTo}
              className="site-btn mt-1.5 flex h-[54px] items-center justify-center rounded-2xl bg-brand-600 font-display text-lg font-extrabold text-white [--press:var(--color-brand-deep)] dark:text-slate-900"
            >
              {loginLabel}
            </Link>
          </div>
        )}
      </header>

      <main id="site-main" className="flex-grow">
        {children}
      </main>

      <footer className="border-t-2 border-line bg-surface">
        {/* Phones: a two-column grid (brand on top, the link groups paired
            below). The wrapping flex row zigzagged at this width. */}
        <div className={`${CONTAINER} grid grid-cols-2 gap-x-5 gap-y-7 py-[26px] sm:flex sm:flex-wrap sm:justify-between sm:gap-7 sm:py-[38px]`}>
          <div className="col-span-2 flex flex-col gap-2.5 sm:min-w-[220px]">
            <span className="flex items-center gap-2.5">
              <ParrotMark size={32} />
              <span className="font-display text-xl font-extrabold">{t('brand')}</span>
            </span>
            <span className="max-w-[32ch] text-[13.5px] leading-[1.8] text-ink-2">{t('footer.tagline')}</span>
          </div>
          <div className="flex flex-col gap-2.5 text-[13.5px] text-ink-2">
            <span className="font-display text-[15px] font-extrabold text-ink-2">{t('footer.product')}</span>
            <Link to="/" className="text-ink-2 hover:text-ink">
              {t('nav.home')}
            </Link>
            <Link to="/download" className="text-ink-2 hover:text-ink">
              {t('footer.download')}
            </Link>
            <Link to="/auth" className="text-ink-2 hover:text-ink">
              {t('nav.login')}
            </Link>
          </div>
          <div className="flex flex-col gap-2.5 text-[13.5px] text-ink-2">
            <span className="font-display text-[15px] font-extrabold text-ink-2">{t('footer.company')}</span>
            <Link to="/contact" className="text-ink-2 hover:text-ink">
              {t('nav.contact')}
            </Link>
            <Link to="/privacy" className="text-ink-2 hover:text-ink">
              {t('nav.privacy')}
            </Link>
          </div>
          <div className="flex flex-col gap-2.5 text-[13.5px] sm:min-w-[170px]">
            <span className="font-display text-[15px] font-extrabold text-ink-2">{t('footer.version')}</span>
            <span className="text-ink-2">{appVersionLine()}</span>
            <span className="text-ink-2">{t('footer.copyright', { year: localizedNumber(new Date().getFullYear(), language) })}</span>
          </div>
          <EnamadSeal />
        </div>
      </footer>

      {guide && <Guide guideKey={scroll.key} visible={scroll.visible} />}
    </div>
  )
}
